/**
 * useImportConfig Hook
 *
 * A React hook for managing import configuration via the API.
 * Provides methods for CRUD operations and sync/test connection.
 */

import { useState, useCallback } from 'react';
import type {
  ImportConfig,
  SyncResult,
  TestConnectionResult,
  ImportConfigFormData,
  ImportConfigValidationError,
} from '@/types/accounting';

/**
 * Convert camelCase form data to snake_case API request body
 */
function toApiRequestBody(data: ImportConfigFormData): Record<string, unknown> {
  return {
    import_config: {
      spreadsheet_id: data.spreadsheetId,
      sheet_name: data.sheetName,
      active: data.active,
    },
  };
}

/**
 * Convert partial update data to snake_case API request body
 */
function toPartialApiRequestBody(data: Partial<ImportConfigFormData>): Record<string, unknown> {
  const importConfig: Record<string, unknown> = {};

  if (data.spreadsheetId !== undefined) {
    importConfig.spreadsheet_id = data.spreadsheetId;
  }
  if (data.sheetName !== undefined) {
    importConfig.sheet_name = data.sheetName;
  }
  if (data.active !== undefined) {
    importConfig.active = data.active;
  }

  return { import_config: importConfig };
}

export interface UseImportConfigReturn {
  /** Current import configuration, null if not fetched yet */
  config: ImportConfig | null;
  /** Whether an API request is in progress */
  loading: boolean;
  /** Error message from the last failed request */
  error: string | null;
  /** Validation errors from create/update operations */
  validationErrors: string[];
  /** Fetch the current import configuration */
  getConfig: () => Promise<ImportConfig>;
  /** Create a new import configuration */
  createConfig: (data: ImportConfigFormData) => Promise<ImportConfig>;
  /** Update the existing import configuration */
  updateConfig: (data: Partial<ImportConfigFormData>) => Promise<ImportConfig>;
  /** Delete the import configuration */
  deleteConfig: () => Promise<void>;
  /** Trigger a manual sync */
  sync: () => Promise<SyncResult>;
  /** Test connection to a spreadsheet */
  testConnection: (spreadsheetId: string, sheetName: string) => Promise<TestConnectionResult>;
  /** Clear any error state */
  clearError: () => void;
}

/**
 * Hook for managing import configuration
 *
 * @example
 * ```typescript
 * const { config, loading, error, getConfig, createConfig, sync } = useImportConfig();
 *
 * // Fetch configuration on mount
 * useEffect(() => {
 *   getConfig();
 * }, [getConfig]);
 *
 * // Create new configuration
 * const handleCreate = async () => {
 *   await createConfig({
 *     spreadsheetId: '1abc123',
 *     sheetName: 'Transactions'
 *   });
 * };
 *
 * // Trigger sync
 * const handleSync = async () => {
 *   const result = await sync();
 *   console.log(`Imported ${result.importedCount} transactions`);
 * };
 * ```
 */
export function useImportConfig(): UseImportConfigReturn {
  const [config, setConfig] = useState<ImportConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const clearError = useCallback(() => {
    setError(null);
    setValidationErrors([]);
  }, []);

  /**
   * Fetch the current import configuration
   */
  const getConfig = useCallback(async (): Promise<ImportConfig> => {
    setLoading(true);
    setError(null);
    setValidationErrors([]);

    try {
      const response = await fetch('/api/accounting/import_config');

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized');
        }
        throw new Error(`Failed to fetch configuration: ${response.statusText}`);
      }

      const data = (await response.json()) as ImportConfig;
      setConfig(data);
      return data;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to fetch configuration';
      setError(message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create a new import configuration
   */
  const createConfig = useCallback(async (data: ImportConfigFormData): Promise<ImportConfig> => {
    setLoading(true);
    setError(null);
    setValidationErrors([]);

    try {
      const response = await fetch('/api/accounting/import_config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(toApiRequestBody(data)),
      });

      if (!response.ok) {
        if (response.status === 422) {
          const errorData = (await response.json()) as ImportConfigValidationError;
          setValidationErrors(errorData.errors);
          throw new Error(errorData.errors.join(', '));
        }
        if (response.status === 401) {
          throw new Error('Unauthorized');
        }
        throw new Error(`Failed to create configuration: ${response.statusText}`);
      }

      const newConfig = (await response.json()) as ImportConfig;
      setConfig(newConfig);
      return newConfig;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to create configuration';
      setError(message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update the existing import configuration
   */
  const updateConfig = useCallback(
    async (data: Partial<ImportConfigFormData>): Promise<ImportConfig> => {
      setLoading(true);
      setError(null);
      setValidationErrors([]);

      try {
        const response = await fetch('/api/accounting/import_config', {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(toPartialApiRequestBody(data)),
        });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('No import configuration found');
          }
          if (response.status === 422) {
            const errorData = (await response.json()) as ImportConfigValidationError;
            setValidationErrors(errorData.errors);
            throw new Error(errorData.errors.join(', '));
          }
          if (response.status === 401) {
            throw new Error('Unauthorized');
          }
          throw new Error(`Failed to update configuration: ${response.statusText}`);
        }

        const updatedConfig = (await response.json()) as ImportConfig;
        setConfig(updatedConfig);
        return updatedConfig;
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Failed to update configuration';
        setError(message);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Delete the import configuration
   */
  const deleteConfig = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    setValidationErrors([]);

    try {
      const response = await fetch('/api/accounting/import_config', {
        method: 'DELETE',
      });

      if (!response.ok && response.status !== 204) {
        if (response.status === 401) {
          throw new Error('Unauthorized');
        }
        throw new Error(`Failed to delete configuration: ${response.statusText}`);
      }

      setConfig({ configured: false });
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to delete configuration';
      setError(message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Trigger a manual sync
   */
  const sync = useCallback(async (): Promise<SyncResult> => {
    setLoading(true);
    setError(null);
    setValidationErrors([]);

    try {
      const response = await fetch('/api/accounting/import_config/sync', {
        method: 'POST',
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('No import configuration found');
        }
        if (response.status === 401) {
          throw new Error('Unauthorized');
        }
        throw new Error(`Failed to sync: ${response.statusText}`);
      }

      const result = (await response.json()) as SyncResult;

      // Refresh the config after sync to get updated lastSyncAt and lastRow
      await getConfig();

      return result;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to sync';
      setError(message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [getConfig]);

  /**
   * Test connection to a spreadsheet
   */
  const testConnection = useCallback(
    async (spreadsheetId: string, sheetName: string): Promise<TestConnectionResult> => {
      setLoading(true);
      setError(null);
      setValidationErrors([]);

      try {
        const response = await fetch('/api/accounting/import_config/test_connection', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            spreadsheet_id: spreadsheetId,
            sheet_name: sheetName,
          }),
        });

        // Handle various error responses
        if (!response.ok) {
          if (response.status === 401) {
            const data = (await response.json()) as TestConnectionResult;
            return data; // Return the error response with success: false
          }
          if (response.status === 422) {
            const data = (await response.json()) as TestConnectionResult;
            return data; // Return the error response with success: false
          }
          throw new Error(`Failed to test connection: ${response.statusText}`);
        }

        return (await response.json()) as TestConnectionResult;
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Failed to test connection';
        setError(message);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    config,
    loading,
    error,
    validationErrors,
    getConfig,
    createConfig,
    updateConfig,
    deleteConfig,
    sync,
    testConnection,
    clearError,
  };
}
